import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quotes } from "@phosphor-icons/react";
import { Reveal } from "@/components/site/Reveal";
import { ReviewForm } from "@/components/site/ReviewForm";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const SEED = [
  {
    id: "seed-1",
    text: "Blew a tire on I-77 at 11pm and the owner picked up on the second ring. Flatbed showed up in under 25 minutes and my lowered Mustang didn't get a scratch.",
    name: "Marcus T.",
    location: "Rock Hill, SC",
    rating: 5,
    photo_url: null,
  },
  {
    id: "seed-2",
    text: "We run a body shop and Twizted is our first call for auction pickups. Reliable, on time, and treats every car like it's his own. Best partner we've had.",
    name: "Diana R.",
    location: "Fort Mill Auto Body",
    rating: 5,
    photo_url: null,
  },
  {
    id: "seed-3",
    text: "No dispatcher runaround — I talked to the actual driver who told me exactly when he'd arrive, and he was early. Fair price and super professional.",
    name: "Kevin B.",
    location: "Tega Cay, SC",
    rating: 5,
    photo_url: null,
  },
];

const Stars = ({ n = 5 }) => (
  <div className="flex gap-1" aria-label={`${n} out of 5 stars`}>
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        weight="fill"
        className={`h-5 w-5 ${i < n ? "text-[var(--tt-green)]" : "text-zinc-700"}`}
      />
    ))}
  </div>
);

const ReviewCard = ({ r, i }) => (
  <Reveal delay={(i % 3) * 0.1}>
    <figure
      data-testid={`review-card-${r.id}`}
      className="group h-full flex flex-col bg-[var(--tt-surface)] border border-white/5 overflow-hidden hover:border-[var(--tt-green)]/50 transition-colors duration-500 tt-clip-card"
    >
      {r.photo_url && (
        <div className="h-48 w-full overflow-hidden border-b border-white/10">
          <img
            src={`${BACKEND}${r.photo_url}`}
            alt={`Tow by Twizted Tow — ${r.name}`}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
        </div>
      )}
      <div className="flex flex-col flex-1 p-8">
        <Quotes
          weight="fill"
          className="h-8 w-8 text-[var(--tt-green)]/30 group-hover:text-[var(--tt-green)] transition-colors duration-500 mb-5"
        />
        <blockquote className="text-zinc-300 text-base leading-relaxed flex-1">"{r.text}"</blockquote>
        <figcaption className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
          <div>
            <p className="font-display uppercase text-lg tracking-tight leading-none">{r.name}</p>
            {r.location && (
              <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 mt-2">
                {r.location}
              </p>
            )}
          </div>
          <Stars n={r.rating} />
        </figcaption>
      </div>
    </figure>
  </Reveal>
);

export const Reviews = () => {
  const [userReviews, setUserReviews] = useState([]);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setUserReviews(Array.isArray(data) ? data : []);
    } catch {
      setUserReviews([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const all = [...userReviews, ...SEED];

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative z-10 bg-[var(--tt-black)] px-5 md:px-10 lg:px-16 py-24 md:py-32 border-t border-white/10"
    >
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 md:mb-20">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--tt-green)] mb-4">
              / Straight From The Roadside
            </p>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tighter">
              Drivers who <span className="text-[var(--tt-green)]">trust the hook.</span>
            </h2>
          </div>
          <div className="flex flex-col items-start md:items-end gap-4">
            <div className="flex items-center gap-4">
              <Stars n={5} />
              <span className="font-display text-2xl md:text-3xl">
                5.0<span className="text-zinc-500 text-lg"> / Google</span>
              </span>
            </div>
            <ReviewForm onSuccess={load} />
          </div>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {all.map((r, i) => (
          <ReviewCard key={r.id} r={r} i={i} />
        ))}
      </div>
    </section>
  );
};
